import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Site configuration types and context used to gate the application startup
 * and provide site-wide dynamic settings.
 */

/**
 * Shape of the APP_CONFIG object injected by the runtime /app-config.js script.
 * This extends the global Window interface for type-safe access.
 */
interface AppConfigGlobal {
  BASE_API_URL?: string;
}

/**
 * Extend the global Window interface to include our runtime config.
 */
declare global {
  interface Window {
    APP_CONFIG?: AppConfigGlobal;
  }
}

/**
 * Shape of Vite environment variables used by this module.
 */
interface ImportMetaEnv {
  readonly VITE_BASE_API_URL?: string;
}

/**
 * Raw configuration payload shape received from the server.
 * Supports multiple naming conventions for backwards compatibility.
 */
interface RawConfigPayload {
  graphqlEndpoint?: string;
  GraphqlEndpoint?: string;
  graphqlUri?: string;
  DiscordAuthEndpoint?: string;
  discordAuthEndpoint?: string;
  discordAuthUri?: string;
}

// Normalized site configuration shape stored and exposed by this context.
// This schema is the single source of truth for consumer code.
export interface SiteConfig {
  /** Fully qualified GraphQL endpoint URI (absolute). */
  graphqlUri: string;
  /** Fully qualified Discord OAuth start endpoint URI (absolute). */
  discordAuthUri: string;
}

/**
 * Value exposed by the SiteConfigContext.
 */
export type SiteConfigContextValue = {
  /** Last loaded normalized site configuration; null until resolved. */
  config: SiteConfig | null;
  /** True while the provider is actively loading configuration. */
  loading: boolean;
  /** Error from the last load attempt, if any. */
  error: Error | null;
  /** Forces a refetch from the server, bypassing cache. */
  refresh: () => Promise<void>;
  /** Alias for refresh for UI affordances (Retry button). */
  retry: () => Promise<void>;
  /** Whether the app is ready to render (config present and not loading). */
  isReady: boolean;
  /** Current TTL used for cache (ms). */
  ttlMs: number;
};

const SiteConfigContext = createContext<SiteConfigContextValue | undefined>(undefined);

/**
 * Props accepted by the SiteConfigProvider component.
 */
export type SiteConfigProviderProps = {
  children: React.ReactNode;
  /** Cache time-to-live in milliseconds (default 10 minutes). */
  ttlMs?: number;
  /** Optional override for the configuration endpoint. */
  configUrlOverride?: string;
};

const LOCAL_STORAGE_KEY = 'siteConfigCache';

/**
 * Default configuration values used as fallbacks when the remote payload
 * is missing fields. All values are absolute.
 */
/**
 * Resolve the base API URL for static initialization.
 * Used only during DefaultConfigurationValues setup.
 */
function resolveBaseUrl(): string {
  const fromWindow = window.APP_CONFIG?.BASE_API_URL;
  if (fromWindow && typeof fromWindow === 'string') return fromWindow;
  const env = import.meta.env as ImportMetaEnv;
  return env.VITE_BASE_API_URL || 'http://localhost:5229';
}

export const DefaultConfigurationValues: SiteConfig = {
  /** Build an absolute GraphQL URI from the base API URL exposed by app-config.js */
  graphqlUri: `${resolveBaseUrl().replace(/\/$/, '')}/graphql`,
  /** Default Discord OAuth start endpoint (absolute) */
  discordAuthUri: `${resolveBaseUrl().replace(/\/$/, '')}/auth/discord`
};

/**
 * Resolve the base API URL. Prefers window.APP_CONFIG (served by /app-config.js),
 * with fallbacks to Vite env and a local default.
 */
function getBaseApiUrl(): string {
  // Prefer window.APP_CONFIG injected by index.html / Vite middleware
  const fromWindow = window.APP_CONFIG?.BASE_API_URL;
  if (fromWindow && typeof fromWindow === 'string') return fromWindow;
  // Fallback: env var or dev default (plain base URL, no /graphql)
  const env = import.meta.env as ImportMetaEnv;
  return env.VITE_BASE_API_URL || 'http://localhost:5229';
}

/**
 * Build the configuration endpoint URL.
 */
function getConfigEndpoint(configUrlOverride?: string): string {
  if (configUrlOverride) return configUrlOverride;
  const base = getBaseApiUrl().replace(/\/$/, '');
  const route = "configuration/site";
  // Backend controller is [Route("configuration")]
  return `${base}/${route}`;
}

/**
 * Normalize a remote/legacy payload into our internal SiteConfig shape.
 * - Accepts GraphQL: `graphqlEndpoint`, `GraphqlEndpoint`, or `graphqlUri`.
 * - Accepts Discord: `DiscordAuthEndpoint`, `discordAuthEndpoint`, or `discordAuthUri`.
 * - Coerces backslashes to forward slashes.
 * - Ensures absolute URLs by combining with BASE_API_URL when needed.
 * - Falls back to DefaultConfigurationValues when missing/invalid.
 *
 * @param raw - The raw configuration payload from server or cache (may be partial or use legacy keys).
 */
function normalizeToSiteConfig(raw: Partial<RawConfigPayload> | null | undefined): SiteConfig {
  const coerceAbs = (val: unknown, defaultUrl: string): string => {
    if (typeof val === 'string' && val.trim()) {
      let ep = val.trim().replace(/\\/g, '/');
      const isAbsolute = /^https?:\/\//i.test(ep);
      if (isAbsolute) return ep;
      const base = getBaseApiUrl().replace(/\/$/, '');
      const path = ep.startsWith('/') ? ep : `/${ep}`;
      return `${base}${path}`;
    }
    return defaultUrl;
  };

  try {
    const graphql = coerceAbs(
      raw?.graphqlEndpoint ?? raw?.GraphqlEndpoint ?? raw?.graphqlUri,
      DefaultConfigurationValues.graphqlUri
    );
    const discord = coerceAbs(
      raw?.DiscordAuthEndpoint ?? raw?.discordAuthEndpoint ?? raw?.discordAuthUri,
      DefaultConfigurationValues.discordAuthUri
    );
    return { graphqlUri: graphql, discordAuthUri: discord };
  } catch {
    return { ...DefaultConfigurationValues };
  }
}

/**
 * Read cached configuration (and timestamp) from localStorage.
 * Handles migration from legacy cached shapes by normalizing on read.
 */
function readCache(): { timestamp: number; data: SiteConfig } | null {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const data = normalizeToSiteConfig(parsed.data);
    return { timestamp: parsed.timestamp, data };
  } catch {
    return null;
  }
}

/**
 * Write configuration to localStorage with a fresh timestamp.
 */
function writeCache(data: SiteConfig) {
  try {
    const payload = { timestamp: Date.now(), data };
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore storage errors (e.g., quota)
  }
}

/**
 * Provider that loads site configuration, caches it with TTL, and exposes
 * readiness state to gate the rest of the application.
 */
export function SiteConfigProvider({ children, ttlMs = 10 * 60 * 1000, configUrlOverride }: SiteConfigProviderProps) {
  const [config, setConfig] = useState<SiteConfig | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);

  const endpoint = useMemo(() => getConfigEndpoint(configUrlOverride), [configUrlOverride]);


  /** Load configuration from the server and update cache. */
  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(endpoint, { credentials: 'include' });
      if (!res.ok) throw new Error(`Failed to fetch site configuration: ${res.status}`);
      const raw = await res.json();
      const normalized = normalizeToSiteConfig(raw);
      setConfig(normalized);
      writeCache(normalized);
    } catch (e: unknown) {
      // On failure, attempt to use cached data as a soft fallback
      const cached = readCache();
      if (cached) {
        console.warn('Using cached site configuration due to fetch error:', e);
        setConfig(cached.data);
      }
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  /** Public refetch that de-duplicates concurrent calls. */
  const refresh = useCallback(async () => {
    // de-duplicate refresh calls
    if (!inFlightRef.current) {
      inFlightRef.current = fetchConfig().finally(() => {
        inFlightRef.current = null;
      });
    }
    return inFlightRef.current;
  }, [fetchConfig]);

  useEffect(() => {
    // On mount: try cache, validate TTL, else fetch
    const cached = readCache();
    if (cached && Date.now() - cached.timestamp < ttlMs) {
      setConfig(cached.data);
      setLoading(false);
    } else {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ttlMs, endpoint]);

  const isReady = !!config && !loading;
  const value = useMemo<SiteConfigContextValue>(
    () => ({ config, loading, error, refresh, retry: refresh, isReady, ttlMs }),
    [config, loading, error, refresh, isReady, ttlMs]
  );

  return <SiteConfigContext.Provider value={value}>{children}</SiteConfigContext.Provider>;
}

/**
 * Hook to read site configuration and readiness state. Must be used under
 * a SiteConfigProvider.
 */
export function useSiteConfig(): SiteConfigContextValue {
  const ctx = useContext(SiteConfigContext);
  if (!ctx) {
    throw new Error('useSiteConfig must be used within a SiteConfigProvider');
  }
  return ctx;
}

/**
 * Small helper hook exposing only the readiness triad for consumers that
 * don't need direct access to the config or refresh functions.
 */
export function useAppReady(): { isReady: boolean; loading: boolean; error: Error | null } {
  const { isReady, loading, error } = useSiteConfig();
  return { isReady, loading, error };
}

/**
 * Convenience hook to get the resolved GraphQL URI from the normalized site configuration.
 * - Returns a fully qualified URL (e.g. "https://api.example.com/graphql") or null if not yet available.
 * - When used under the startup gate after readiness, this will be a non-null absolute URL.
 */
export function useGraphqlUri(): string | null {
  const { config } = useSiteConfig();
  return config?.graphqlUri ?? null;
}

/**
 * Convenience hook to get the resolved Discord OAuth start endpoint from the normalized site configuration.
 * - Returns a fully qualified URL string or null if not yet available.
 * - Consumers should append any required query parameters (e.g., redirectUri) as needed.
 */
export function useDiscordAuthUri(): string | null {
  const { config } = useSiteConfig();
  return config?.discordAuthUri ?? null;
}
